import React, { useState, useEffect } from 'react';
import { Card, Steps, Button, Typography, Row, Col, Space, Tag, Input, Select, Alert, message, Spin, InputNumber, Checkbox, Collapse, Grid } from 'antd';
import {
  CheckCircleOutlined,
  RocketOutlined,
  SendOutlined,
  ThunderboltOutlined,
  FireOutlined,
  LinkOutlined,
  CopyOutlined,
  EditOutlined,
  ShopOutlined,
  ArrowLeftOutlined,
  MailOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth } from '../hooks/useAuth';
import {
  useGetBrandsQuery,
  useCompleteOnboardingMutation,
  useCreateSurveyMutation,
  useGetSurveyCohortsQuery,
  useUpdateBrandNameMutation,
  useGetSurveysQuery,
  useGetSurveyConfigQuery,
  useGetTrialStatusQuery
} from '../services/nestjs-base-api';
import { TemplateTypeEnum } from '../enums/TemplateTypeEnum';
import { Utils } from '../utils/utils';
import { GettingStartedGuide } from './MarketerView/components/GettingStartedGuide';
import '../styles/revolutionary-dashboard.css';

const { useBreakpoint } = Grid;
const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

interface OnboardingWizardProps {
  onComplete: () => void;
  forceStep?: number;
  // Override the "View all surveys" navigation. Defaults to /dashboard/surveys;
  // marketer-view passes a handler that stays inside its own shell.
  onViewSurveys?: () => void;
  // Read-only recall (e.g. shown in a modal from the marketer-view): strip the
  // onboarding chrome — the "Quick Setup Wizard" hero, the step progress bar,
  // the left decorative pane, and the step-3 completion footer — leaving just
  // the reference content.
  embedded?: boolean;
}

export const SimpleOnboardingWizardV2: React.FC<OnboardingWizardProps> = ({ onComplete, forceStep, onViewSurveys, embedded }) => {
  const intl = useIntl();
  const { user } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Get default welcome message from intl
  const defaultWelcomeMessage = intl.formatMessage({ id: 'onboarding.survey.welcomeMessageDefault' });

  // Get initial state ONCE and memoize it
  // Include user/org ID in the key to prevent data leakage between users
  const sessionStorageKey = `simpleOnboardingWizardStateV2_${user?.organisation?.id || 'unknown'}`;

  const initialState = React.useMemo(() => {
    const savedWizardState = sessionStorage.getItem(sessionStorageKey);
    const state = savedWizardState && forceStep === undefined
      ? JSON.parse(savedWizardState)
      : {
          currentStep: forceStep !== undefined ? forceStep : 0,
          brandNameConfirmed: forceStep !== undefined && forceStep > 0,
          surveyCreated: forceStep === 2,
          createdSurveyHash: forceStep === 2 ? 'demo-hash' : '',
          surveyName: forceStep === 2 ? 'Demo Survey' : '',
          surveyDescription: forceStep === 2 ? 'This is a demo survey for testing purposes' : defaultWelcomeMessage,
          businessModel: forceStep === 2 ? 'dtc' : '',
          productType: forceStep === 2 ? 'durable' : '',
          questionsPerParticipant: 12,
          createdSurveys: forceStep === 2 ? [] : [],
          brandName: ''
        };

    // If surveyDescription is empty/undefined, use default
    if (!state.surveyDescription || state.surveyDescription.trim() === '') {
      state.surveyDescription = defaultWelcomeMessage;
    }

    // If surveys have been created, skip to step 3 (share step)
    if (state.createdSurveys && state.createdSurveys.length > 0 && forceStep === undefined) {
      state.currentStep = 2;
      state.surveyCreated = true;
      state.brandNameConfirmed = true;
    }

    return state;
  }, [defaultWelcomeMessage, forceStep]);

  const [currentStep, setCurrentStep] = useState(initialState.currentStep);
  const [completeOnboarding] = useCompleteOnboardingMutation();
  const [createSurvey] = useCreateSurveyMutation({
    fixedCacheKey: 'onboarding-survey-creation'
  });
  const [updateBrandName] = useUpdateBrandNameMutation();
  const [brandNameConfirmed, setBrandNameConfirmed] = useState(initialState.brandNameConfirmed);
  const [surveyCreated, setSurveyCreated] = useState(initialState.surveyCreated);
  const [createdSurveys, setCreatedSurveys] = useState<Array<{ hash: string; cohortId: number }>>(
    initialState.createdSurveys || []
  );
  const [isCreatingSurvey, setIsCreatingSurvey] = useState(false);
  const [isUpdatingBrandName, setIsUpdatingBrandName] = useState(false);
  const [firstBatchCommitted, setFirstBatchCommitted] = useState(false);

  // Form data
  const [brandName, setBrandName] = useState(initialState.brandName);
  const [surveyDescription, setSurveyDescription] = useState(initialState.surveyDescription);
  const [businessModel, setBusinessModel] = useState<string | undefined>(initialState.businessModel || undefined);
  const [productType, setProductType] = useState<string | undefined>(initialState.productType || undefined);
  const [questionsPerParticipant, setQuestionsPerParticipant] = useState(initialState.questionsPerParticipant);

  // Fetch fixed statements per response setting
  const { data: surveyConfig } = useGetSurveyConfigQuery();
  const fixedStatements = surveyConfig?.fixedStatementsPerResponse || 0;

  useEffect(() => {
    if (fixedStatements > 0) {
      setQuestionsPerParticipant(fixedStatements);
    }
  }, [fixedStatements]);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Save state to session storage whenever it changes.
  // A forced-step instance (the recallable "getting started" guide opened from
  // the Briefing) is READ-ONLY: it shares this sessionStorage key with the real
  // wizard, so persisting from it overwrites the user's actual progress with the
  // forced step's synthetic values (step 3, surveyCreated: true, an empty
  // createdSurveys, and placeholder dtc/durable) — which then strands the real
  // wizard on the step-3 spinner.
  useEffect(() => {
    if (forceStep !== undefined) return;

    const stateToSave = {
      currentStep,
      brandNameConfirmed,
      surveyCreated,
      createdSurveys,
      brandName,
      surveyDescription,
      businessModel,
      productType,
      questionsPerParticipant
    };
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(stateToSave));
  }, [currentStep, brandNameConfirmed, surveyCreated, createdSurveys, brandName, surveyDescription, businessModel, productType, questionsPerParticipant, sessionStorageKey, forceStep]);

  // Fetch brands 
  const { data: brands = [], isLoading: brandsLoading } = useGetBrandsQuery({
    jwtToken: user?.jwtToken || '',
    organisationId: user?.organisation?.id || 0,
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  // Fetch cohorts
  const { data: cohorts = [], isLoading: cohortsLoading } = useGetSurveyCohortsQuery({
    jwtToken: user?.jwtToken || '',
    organisationId: user?.organisation?.id || 0,
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  // Fetch surveys to check if any exist
  const { data: surveysData = [], isSuccess: surveysLoaded } = useGetSurveysQuery({
    jwtToken: user?.jwtToken || '',
    organisationId: user?.organisation?.id || 0,
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  // Fetch free trial status — values come from organisation_subscriptions.metadata
  // snapshotted at trial start, so the copy on the final step reflects the actual
  // trial parameters this org signed up under, not whatever the current system
  // settings happen to be.
  const { data: trialStatus } = useGetTrialStatusQuery({
    jwtToken: user?.jwtToken || '',
    orgId: String(user?.organisation?.id || ''),
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  // Check if there are any active surveys
  const hasActiveSurveys = surveysData.some((s: any) => s.status === 'active' && !s.deletedAt);

  // Get the first/primary brand
  const primaryBrand = brands[0];

  // Set brand name from primaryBrand when it's loaded
  useEffect(() => {
    if (primaryBrand && !brandName) {
      setBrandName(primaryBrand.name);
    }
  }, [primaryBrand, brandName]);

  // API already returns only active system cohorts + this organization's brand cohorts
  const activeCohorts = cohorts;

  // Self-heal a step-3 state that has no surveys behind it. Step 3 renders the
  // "Creating surveys..." spinner whenever createdSurveys is empty, so such a
  // state is a permanent dead end: the user sees an endless spinner, can't get
  // back, and never creates a survey. Restored sessionStorage is the way in
  // (see the read-only guard on the save effect above). Only act once the
  // surveys query has actually resolved, so a slow load isn't mistaken for
  // "no surveys".
  useEffect(() => {
    if (forceStep !== undefined) return;
    if (currentStep !== 2 || isCreatingSurvey) return;
    if (createdSurveys.length > 0) return;
    if (!surveysLoaded || hasActiveSurveys) return;

    console.warn('Onboarding: step 3 with no surveys — returning to survey setup');
    setSurveyCreated(false);
    setCurrentStep(1);
  }, [forceStep, currentStep, isCreatingSurvey, createdSurveys.length, surveysLoaded, hasActiveSurveys]);

  // If active surveys exist, populate the createdSurveys array
  // And skip to step 3 (but only if not forcing a specific step via URL parameter)
  useEffect(() => {
    if (hasActiveSurveys && surveysData.length > 0) {
      // Build createdSurveys array from actual survey data
      const surveysList = surveysData
        .filter((s: any) => s.status === 'active' && !s.deletedAt)
        .map((s: any) => ({ hash: s.hash, cohortId: s.cohort?.id || s.cohortId }));

      if (surveysList.length > 0 && createdSurveys.length === 0) {
        setCreatedSurveys(surveysList);
        setSurveyCreated(true);
        setBrandNameConfirmed(true);

        // Only auto-navigate if not forcing a specific step
        if (currentStep < 2 && forceStep === undefined) {
          setCurrentStep(2);
        }
      }
    }
  }, [hasActiveSurveys, surveysData, currentStep, forceStep, createdSurveys.length]);

  // Auto-select Durable Goods for 3P Retailer
  useEffect(() => {
    if (businessModel === '3p_retailer' && productType !== TemplateTypeEnum.THREE_P_DURABLE) {
      setProductType(TemplateTypeEnum.THREE_P_DURABLE);
    }
  }, [businessModel, productType]);

  // Pre-fill from organisation's templateType (set during registration pre-qualification)
  const orgTemplateType = user?.organisation?.templateType;
  const hasOrgTemplateType = !!orgTemplateType;

  useEffect(() => {
    if (orgTemplateType && !businessModel && !productType) {
      setProductType(orgTemplateType);
      setBusinessModel(orgTemplateType === TemplateTypeEnum.THREE_P_DURABLE ? '3p_retailer' : 'dtc');
    }
  }, [orgTemplateType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStep1Submit = async () => {
    const newErrors: Record<string, string> = {};

    // Validate brand name
    if (!brandName || brandName.trim() === '') {
      newErrors.brandName = intl.formatMessage({ id: 'onboarding.brandName.required' });
    }

    // Validate business model
    if (!businessModel) {
      newErrors.businessModel = intl.formatMessage({ id: 'onboarding.brandName.businessModelRequired' });
    }

    // Validate product type
    if (!productType) {
      newErrors.productType = intl.formatMessage({ id: 'onboarding.brandName.productTypeRequired' });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsUpdatingBrandName(true);
    setErrors({});

    try {
      // Only update if the brand name has changed
      if (primaryBrand && brandName !== primaryBrand.name) {
        await updateBrandName({
          jwtToken: user?.jwtToken!,
          organisationId: user?.organisation?.id!,
          brandId: primaryBrand.id,
          name: brandName.trim()
        }).unwrap();

        message.success(intl.formatMessage({ id: 'onboarding.brandName.updateSuccess' }));
      }

      // Mark brand name as confirmed and move to next step
      setBrandNameConfirmed(true);
      setCurrentStep(1);
      setIsUpdatingBrandName(false);
    } catch (error: any) {
      console.error('Failed to update brand name:', error);
      message.error(intl.formatMessage({ id: 'onboarding.brandName.updateError' }));
      setIsUpdatingBrandName(false);
    }
  };

  const validateSurveyForm = () => {
    const newErrors: Record<string, string> = {};

    // Double check these (should be set from step 1)
    if (!businessModel) {
      newErrors.general = 'Business model is missing. Please go back to step 1.';
    }

    if (!productType) {
      newErrors.general = 'Product type is missing. Please go back to step 1.';
    }

    if (!primaryBrand) {
      newErrors.general = 'Unable to create surveys: missing brand data';
    }

    if (!activeCohorts || activeCohorts.length === 0) {
      newErrors.general = 'Unable to create surveys: no active cohorts found';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSurvey = async () => {
    // Clear previous errors
    setErrors({});

    // Validate inputs
    if (!validateSurveyForm()) {
      console.error('Form validation failed');
      return;
    }

    setIsCreatingSurvey(true);

    try {
      // Auto-generate survey name
      const surveyName = `${primaryBrand.name} Brand Survey`;

      // Create surveys for all active cohorts
      const createdSurveyData: Array<{ hash: string; cohortId: number }> = [];

      for (const cohort of activeCohorts) {
        const payload: any = {
          name: surveyName,
          brandId: primaryBrand.id,
          cohortId: cohort.id,
          description: surveyDescription || undefined,
          startsAt: undefined,
          endsAt: undefined,
          questionsPerResponse: questionsPerParticipant,
          rotationStrategy: 'balanced' as const,
          theme: {
            primaryColor: '#4338ca',
            secondaryColor: '#1e40af',
            backgroundColor: '#1e293b',
            cardColor: '#334155',
            accentColor: '#F59E0B'
          },
          showProgressBar: true,
          animationSpeed: 800,
          allowSkip: false,
          allowBackNavigation: true,
          persistProgress: true,
          expirationHours: 24,
          completionTitle: 'Survey Complete!',
          completionSubtitle: 'Thank you for taking the time to share your feedback...',
          completionButtonText: 'Visit our website',
          completionButtonLink: undefined,
          showCompletionButton: false,
          completionSecondaryButtonText: 'Leave us a review',
          completionSecondaryButtonLink: undefined,
          showCompletionSecondaryButton: false,
          collectEmail: true,
          emailRequired: true,
          emailPlaceholder: 'Enter your email address',
          npsRequired: true,
          showWelcomePage: true,
          productType: productType
        };

        const result = await createSurvey({
          jwtToken: user?.jwtToken!,
          organisationId: user?.organisation?.id!,
          payload
        }).unwrap();

        createdSurveyData.push({ hash: result.hash, cohortId: cohort.id });
      }

      // Save wizard state to sessionStorage IMMEDIATELY
      const newState = {
        currentStep: 2,
        brandNameConfirmed: true,
        surveyCreated: true,
        createdSurveys: createdSurveyData,
        brandName,
        surveyDescription,
        businessModel,
        productType,
        questionsPerParticipant
      };
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(newState));

      // Update React state
      setCreatedSurveys(createdSurveyData);
      setSurveyCreated(true);
      setCurrentStep(2);
      setIsCreatingSurvey(false);

      message.success(`${createdSurveyData.length} surveys created successfully!`);
    } catch (error: any) {
      console.error('Failed to create surveys:', error);
      message.error('Failed to create surveys. Please try again or use the full survey creation page.');
      setIsCreatingSurvey(false);
    }
  };

  const handleFinalComplete = async () => {
    // Mark onboarding as completed in the backend
    if (user?.jwtToken) {
      try {
        await completeOnboarding({ jwtToken: user.jwtToken });
        // Clear the session storage (using org-scoped key)
        sessionStorage.removeItem(sessionStorageKey);
        onComplete();
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
      }
    }
  };

  const handleViewSurveyDetails = () => {
    // Marketer-view passes a handler that stays inside its shell; the dashboard
    // onboarding falls back to the surveys list page.
    if (onViewSurveys) {
      onViewSurveys();
      return;
    }
    navigate('/dashboard/surveys');
  };

  const handleCopyEmailTemplate = async (surveyHash: string) => {
    const url = Utils.getPublicSurveyUrl(surveyHash);
    const brandDisplayName = primaryBrand?.name || brandName || '[Your Brand]';
    const subject = intl.formatMessage({
      id: 'onboarding.survey.finalStep.emailTemplateSubject',
    });
    const body = intl.formatMessage(
      { id: 'onboarding.survey.finalStep.emailTemplateBody' },
      { url, brandName: brandDisplayName },
    );
    const fullText = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(fullText);
      message.success(
        intl.formatMessage({
          id: 'onboarding.survey.finalStep.templateCopiedSuccess',
        }),
      );
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: 'onboarding.survey.finalStep.templateCopyError',
        }),
      );
    }
  };

  // Helper function to get cohort display name
  const getCohortDisplayName = (cohortId: number): string => {
    const cohort = cohorts.find(c => c.id === cohortId);
    if (!cohort) return 'Unknown Cohort';

    // Show "All Customers" instead of "Random Customers"
    if (cohort.code === 'random_customers') {
      return 'All Customers';
    }

    return cohort.name;
  };

  const getCohortDescription = (cohortId: number): string => {
    const cohort = cohorts.find(c => c.id === cohortId);
    return cohort?.description || '';
  };

  const steps = [
    {
      title: intl.formatMessage({ id: 'onboarding.brandName.stepTitleMain' }),
      icon: <EditOutlined style={{ fontSize: '20px' }} />,
      completed: brandNameConfirmed,
      content: (
        <Card
          className=""
          style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(245,158,11,.12)'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '500px' }}>
            <Col xs={24} md={10} style={{
              background: '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <ShopOutlined style={{ fontSize: '80px', color: '#F59E0B' }} />
                <div style={{ marginTop: '32px', maxWidth: '300px' }}>
                  <Title level={4} style={{
                    fontSize: '22px',
                    color: '#0F172A',
                    fontWeight: '700',
                    lineHeight: '1.4',
                    margin: 0,
                    marginBottom: '8px'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.brandName.leftTitle' })}
                  </Title>
                  <Text style={{
                    fontSize: '16px',
                    color: '#64748B',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    display: 'block'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.brandName.leftSubtitle' })}
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={14} style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {/* No content-title: the step progress bar above already labels
                  this "Tell us about your business". */}
              <Alert
                type="info"
                icon={<InfoCircleOutlined style={{ color: '#F59E0B' }} />}
                message={intl.formatMessage({ id: 'onboarding.brandName.alertTitle' })}
                description={intl.formatMessage({ id: 'onboarding.brandName.alertDescription' })}
                showIcon
                style={{ marginBottom: '24px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a' }}
              />

              {brandsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px', color: '#64748B' }}>
                    {intl.formatMessage({ id: 'onboarding.brandName.loading' })}
                  </div>
                </div>
              ) : !primaryBrand ? (
                <Alert
                  type="error"
                  message={intl.formatMessage({ id: 'onboarding.brandName.errorSetup' })}
                  description={intl.formatMessage({ id: 'onboarding.brandName.errorSetupDescription' })}
                  showIcon
                />
              ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#0F172A' }}>
                      {intl.formatMessage({ id: 'onboarding.brandName.label' })} <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Input
                      size="large"
                      value={brandName}
                      onChange={(e) => {
                        setBrandName(e.target.value);
                        if (errors.brandName) {
                          setErrors(prev => ({ ...prev, brandName: '' }));
                        }
                      }}
                      placeholder={intl.formatMessage({ id: 'onboarding.brandName.placeholder' })}
                      status={errors.brandName ? 'error' : ''}
                      style={{ marginBottom: errors.brandName ? '4px' : '4px' }}
                    />
                    {errors.brandName && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '4px' }}>
                        {errors.brandName}
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {intl.formatMessage({ id: 'onboarding.brandName.helpText' })}
                    </Text>
                  </div>

                  {/* Preview section */}
                  <Card
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: 16,
                      boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                      marginTop: '16px',
                      marginBottom: '16px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        background: '#F59E0B',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CheckCircleOutlined style={{ fontSize: '20px', color: 'white' }} />
                      </div>
                      <Text strong style={{ fontSize: '15px', color: '#F59E0B' }}>
                        {intl.formatMessage({ id: 'onboarding.brandName.previewTitle' })}
                      </Text>
                    </div>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginLeft: '8px' }}>
                        {intl.formatMessage({ id: 'onboarding.brandName.previewLabel' })}
                      </Text>
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '12px', color: '#0F172A', lineHeight: '1.6' }}>
                          "{intl.formatMessage(
                            { id: 'onboarding.brandName.preview1' },
                            { brandName: <strong style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{brandName || intl.formatMessage({ id: 'onboarding.brandName.previewPlaceholder' })}</strong> }
                          )}"
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '12px', color: '#0F172A', lineHeight: '1.6' }}>
                          "{intl.formatMessage(
                            { id: 'onboarding.brandName.preview2' },
                            { brandName: <strong style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{brandName || intl.formatMessage({ id: 'onboarding.brandName.previewPlaceholder' })}</strong> }
                          )}"
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '500', color: '#0F172A', lineHeight: '1.6' }}>
                          "{intl.formatMessage(
                            { id: 'onboarding.brandName.preview3' },
                            { brandName: <strong style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{brandName || intl.formatMessage({ id: 'onboarding.brandName.previewPlaceholder' })}</strong> }
                          )}"
                        </div>
                      </div>
                    </Space>
                  </Card>

                  {!hasOrgTemplateType && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#0F172A' }}>
                      {intl.formatMessage({ id: 'onboarding.brandName.businessModelLabel' })} <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Select
                      value={businessModel || undefined}
                      onChange={(value) => {
                        setBusinessModel(value);
                        // Clear error when user selects an option
                        if (errors.businessModel) {
                          setErrors(prev => ({ ...prev, businessModel: '' }));
                        }
                        // Reset product type when business model changes
                        setProductType(undefined);
                      }}
                      placeholder={intl.formatMessage({ id: 'onboarding.brandName.businessModelPlaceholder' })}
                      size="large"
                      style={{ width: '100%', marginBottom: errors.businessModel ? '4px' : '4px' }}
                      status={errors.businessModel ? 'error' : ''}
                      optionLabelProp="label"
                    >
                      <Select.Option value="dtc" label={intl.formatMessage({ id: 'onboarding.brandName.businessModelDTC' })}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{intl.formatMessage({ id: 'onboarding.brandName.businessModelDTC' })}</div>
                          <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{intl.formatMessage({ id: 'onboarding.brandName.businessModelDTCDesc' })}</div>
                        </div>
                      </Select.Option>
                      <Select.Option value="3p_retailer" label={intl.formatMessage({ id: 'onboarding.brandName.businessModel3P' })}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{intl.formatMessage({ id: 'onboarding.brandName.businessModel3P' })}</div>
                          <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{intl.formatMessage({ id: 'onboarding.brandName.businessModel3PDesc' })}</div>
                        </div>
                      </Select.Option>
                    </Select>
                    {errors.businessModel && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '4px' }}>
                        {errors.businessModel}
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {businessModel === 'dtc'
                        ? intl.formatMessage({ id: 'onboarding.brandName.businessModelDTCHelp' })
                        : businessModel === '3p_retailer'
                        ? intl.formatMessage({ id: 'onboarding.brandName.businessModel3PHelp' })
                        : intl.formatMessage({ id: 'onboarding.brandName.businessModelHelpDefault' })}
                    </Text>
                  </div>
                  )}

                  {!hasOrgTemplateType && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#0F172A' }}>
                      {intl.formatMessage({ id: 'onboarding.brandName.productTypeLabel' })} <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Select
                      value={productType || undefined}
                      onChange={(value) => {
                        setProductType(value);
                        // Clear error when user selects an option
                        if (errors.productType) {
                          setErrors(prev => ({ ...prev, productType: '' }));
                        }
                      }}
                      placeholder={!businessModel
                        ? intl.formatMessage({ id: 'onboarding.brandName.productTypePlaceholderFirst' })
                        : intl.formatMessage({ id: 'onboarding.brandName.productTypePlaceholderReady' })}
                      size="large"
                      disabled={!businessModel}
                      style={{ width: '100%', marginBottom: errors.productType ? '4px' : '4px' }}
                      status={errors.productType ? 'error' : ''}
                      optionLabelProp="label"
                    >
                      {businessModel === '3p_retailer' ? (
                        <Select.Option value={TemplateTypeEnum.THREE_P_DURABLE} label={intl.formatMessage({ id: 'onboarding.brandName.productTypeDurable' })}>
                          <div>
                            <div style={{ fontWeight: '500' }}>{intl.formatMessage({ id: 'onboarding.brandName.productTypeDurable' })}</div>
                            <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{intl.formatMessage({ id: 'onboarding.brandName.productTypeDurableDesc' })}</div>
                          </div>
                        </Select.Option>
                      ) : (
                        <>
                          <Select.Option value={TemplateTypeEnum.DURABLE} label={intl.formatMessage({ id: 'onboarding.brandName.productTypeDurable' })}>
                            <div>
                              <div style={{ fontWeight: '500' }}>{intl.formatMessage({ id: 'onboarding.brandName.productTypeDurable' })}</div>
                              <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{intl.formatMessage({ id: 'onboarding.brandName.productTypeDurableDesc' })}</div>
                            </div>
                          </Select.Option>
                          <Select.Option value={TemplateTypeEnum.PERISHABLE} label={intl.formatMessage({ id: 'onboarding.brandName.productTypePerishable' })}>
                            <div>
                              <div style={{ fontWeight: '500' }}>{intl.formatMessage({ id: 'onboarding.brandName.productTypePerishable' })}</div>
                              <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{intl.formatMessage({ id: 'onboarding.brandName.productTypePerishableDesc' })}</div>
                            </div>
                          </Select.Option>
                        </>
                      )}
                    </Select>
                    {errors.productType && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '4px' }}>
                        {errors.productType}
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      This determines which question set will be used for your survey
                    </Text>
                  </div>
                  )}

                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined style={{ marginRight: 8 }} />}
                    onClick={handleStep1Submit}
                    loading={isUpdatingBrandName}
                    style={{
                      background: '#F59E0B',
                      border: 'none',
                      height: '48px',
                      fontWeight: '600',
                      fontSize: '16px',
                      width: '100%',
                      color: 'white',
                      borderRadius: 10
                    }}
                    className=""
                  >
                    Confirm & Continue
                  </Button>
                </Space>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
    {
      title: activeCohorts.length === 1
        ? intl.formatMessage({ id: 'onboarding.survey.stepTitleSingle' })
        : intl.formatMessage({ id: 'onboarding.survey.stepTitleMultiple' }),
      icon: <FormOutlined style={{ fontSize: '20px' }} />,
      completed: surveyCreated,
      content: (
        <Card 
          className=""
          style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(245,158,11,.12)'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '500px' }}>
            <Col xs={24} md={10} style={{
              background: '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '24px 16px' : '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <RocketOutlined style={{ fontSize: '80px', color: '#F59E0B' }} />
                <div style={{ marginTop: '32px', maxWidth: '300px' }}>
                  <Title level={4} style={{
                    fontSize: '22px',
                    color: '#0F172A',
                    fontWeight: '700',
                    lineHeight: '1.4',
                    margin: 0,
                    marginBottom: '8px'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.survey.leftPaneTitle' })}
                  </Title>
                  <Text style={{
                    fontSize: '16px',
                    color: '#64748B',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    display: 'block'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.survey.leftPaneSubtitle' })}
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={14} style={{ padding: isMobile ? '24px 16px' : '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {/* No content-title here: the step progress bar above already
                  labels this step "We'll build your surveys". A second identical
                  heading inches below it was pure duplication. */}

              {/* The survey step is where signups bail — the data shows the
                  "automatic / launch" framing reads as "this will email my
                  customers". State plainly that nothing sends without them. */}
              <Alert
                type="success"
                showIcon
                icon={<SafetyOutlined style={{ color: '#16a34a' }} />}
                style={{
                  marginBottom: 20,
                  borderRadius: 10,
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                }}
                message={intl.formatMessage({ id: 'onboarding.survey.nothingSentTitle' })}
                description={
                  <div style={{ fontSize: '13px' }}>
                    {intl.formatMessage({ id: 'onboarding.survey.nothingSentReassurance' })}
                  </div>
                }
              />

              {brandsLoading || cohortsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px', color: '#64748B' }}>Loading setup data...</div>
                </div>
              ) : !primaryBrand || activeCohorts.length === 0 ? (
                <Alert
                  type="error"
                  message={intl.formatMessage({ id: 'onboarding.survey.setupError' })}
                  description={intl.formatMessage({ id: 'onboarding.survey.setupErrorDescription' })}
                  showIcon
                />
              ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {activeCohorts.length === 1 ? (
                    <Alert
                      type="info"
                      icon={<InfoCircleOutlined style={{ color: '#F59E0B' }} />}
                      message={intl.formatMessage(
                        { id: 'onboarding.survey.surveyNameInfo' },
                        { brandName: primaryBrand.name }
                      )}
                      showIcon
                      style={{ marginBottom: '8px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a' }}
                    />
                  ) : (
                    <Alert
                      type="info"
                      icon={<InfoCircleOutlined style={{ color: '#F59E0B' }} />}
                      message={intl.formatMessage({ id: 'onboarding.survey.multipleCohortTitle' })}
                      description={
                        <div style={{ fontSize: '13px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            {intl.formatMessage({ id: 'onboarding.survey.multipleCohortIntro' })}
                          </div>
                          <ul style={{ marginBottom: '8px', paddingLeft: '20px' }}>
                            <li>{intl.formatMessage({ id: 'onboarding.survey.multipleCohortBullet1' })}</li>
                            <li>{intl.formatMessage({ id: 'onboarding.survey.multipleCohortBullet2' })}</li>
                          </ul>
                        </div>
                      }
                      showIcon
                      style={{ marginBottom: '8px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a' }}
                    />
                  )}

                  <div style={{ marginBottom: '8px' }}>
                    <Text style={{ fontSize: '14px', color: '#0F172A', display: 'block' }}>
                      {intl.formatMessage({ id: 'onboarding.survey.introText' })}
                    </Text>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#0F172A' }}>
                      {intl.formatMessage({ id: 'onboarding.survey.welcomeMessageLabel' })}
                    </label>
                    <TextArea
                      value={surveyDescription}
                      onChange={(e) => setSurveyDescription(e.target.value)}
                      rows={3}
                      maxLength={500}
                      showCount
                      style={{ marginBottom: '20px' }}
                    />
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        {intl.formatMessage({ id: 'onboarding.survey.welcomeMessageHelp' })}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', fontStyle: 'italic', marginTop: '2px', color: '#F59E0B' }}>
                        💡 {intl.formatMessage({ id: 'onboarding.survey.welcomeMessageRecommended' })}
                      </Text>
                    </div>
                  </div>

                  {fixedStatements === 0 && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#0F172A' }}>
                      {intl.formatMessage({ id: 'onboarding.survey.questionsPerParticipantLabel' })}
                    </label>
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px' }}>
                      <InputNumber
                        value={questionsPerParticipant}
                        onChange={(value) => {
                          const num = Number(value);
                          if (value === null || isNaN(num)) return;
                          setQuestionsPerParticipant(Math.min(15, Math.max(5, num)));
                        }}
                        min={5}
                        max={15}
                        size="large"
                        controls
                        keyboard
                        inputMode="numeric"
                        style={{ width: isMobile ? '100%' : '120px' }}
                      />
                      <Text type="secondary" style={{ fontSize: '13px', flex: 1 }}>
                        {intl.formatMessage({ id: 'onboarding.survey.questionsPerParticipantHelp' })}
                      </Text>
                    </div>
                  </div>
                  )}

                  {errors.general && (
                    <Alert
                      type="error"
                      message={errors.general}
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  )}

                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: '12px', width: '100%' }}>
                    <Button
                      size="large"
                      icon={<ArrowLeftOutlined />}
                      onClick={() => setCurrentStep(0)}
                      style={{
                        height: '48px',
                        fontWeight: '600',
                        fontSize: '16px',
                        borderRadius: 10,
                        border: '1px solid #E5E7EB',
                        color: '#64748B',
                        padding: '0 24px',
                        width: isMobile ? '100%' : 'auto'
                      }}
                      className="hover-scale"
                    >
                      {intl.formatMessage({ id: 'onboarding.survey.backButton' })}
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      icon={<ThunderboltOutlined />}
                      onClick={handleCreateSurvey}
                      loading={isCreatingSurvey}
                      style={{
                        background: '#F59E0B',
                        border: 'none',
                        height: '48px',
                        fontWeight: '600',
                        fontSize: '16px',
                        minWidth: isMobile ? undefined : '200px',
                        width: isMobile ? '100%' : 'auto',
                        color: 'white',
                        borderRadius: 10
                      }}
                      className=""
                    >
                      {activeCohorts.length === 1
                        ? intl.formatMessage({ id: 'onboarding.survey.buttonCreateSingle' })
                        : intl.formatMessage({ id: 'onboarding.survey.buttonCreateMultiple' }, { count: activeCohorts.length })}
                    </Button>
                  </div>
                </Space>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
    {
      title: activeCohorts.length === 1
        ? intl.formatMessage({ id: 'onboarding.survey.shareTitleSingle' })
        : intl.formatMessage({ id: 'onboarding.survey.shareTitleMultiple' }),
      icon: <SendOutlined style={{ fontSize: '20px' }} />,
      completed: false,
      content: (
        <Card
          className=""
          style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 16,
            boxShadow: '0 8px 30px rgba(245,158,11,.12)',
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '500px' }}>
            {!embedded && (
            <Col xs={24} md={10} style={{
              background: '#FEF3C7',
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              padding: 0,
            }}>
              <div style={{
                position: 'sticky',
                top: '80px',
                padding: isMobile ? '24px 16px' : '200px 40px 40px',
                textAlign: 'center',
              }}>
                <SendOutlined style={{ fontSize: '80px', color: '#F59E0B' }} />
                <div style={{ marginTop: '32px', maxWidth: '300px', marginLeft: 'auto', marginRight: 'auto' }}>
                  <Title level={4} style={{
                    fontSize: '22px',
                    color: '#0F172A',
                    fontWeight: '700',
                    lineHeight: '1.4',
                    margin: 0,
                    marginBottom: '8px'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.survey.shareLeftPaneTitle' })}
                  </Title>
                  <Text style={{
                    fontSize: '16px',
                    color: '#64748B',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    display: 'block'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.survey.shareLeftPaneSubtitle' })}
                  </Text>
                </div>
              </div>
            </Col>
            )}
            <Col xs={24} md={embedded ? 24 : 14} style={{ padding: isMobile ? '24px 16px' : '40px' }}>
              {createdSurveys.length > 0 ? (() => {
                const allCustomersSurvey = createdSurveys.find(s => {
                  const cohort = cohorts.find(c => c.id === s.cohortId);
                  return cohort?.code === 'random_customers';
                });
                const recentPurchasersSurvey = createdSurveys.find(s => {
                  const cohort = cohorts.find(c => c.id === s.cohortId);
                  return cohort?.code === 'recent_purchasers';
                });

                // Trial params come from organisation_subscriptions.metadata via
                // the trial-status endpoint. The urgency banner below is only
                // rendered once the query has resolved with real data — we
                // never surface fallback/default numbers to the user.
                const trialParams = trialStatus
                  ? {
                      totalDays: trialStatus.totalDays,
                      responseCheckDays: trialStatus.responseRequirement.totalDays,
                      requiredResponses: trialStatus.responseRequirement.required,
                    }
                  : null;

                return (
                  <>
                    {/* Hero */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <FireOutlined style={{ color: '#F59E0B', fontSize: '28px' }} />
                        <Title level={3} style={{ color: '#18216d', margin: 0, fontWeight: 700 }}>
                          {intl.formatMessage({ id: 'onboarding.survey.finalStep.heroTitle' })}
                        </Title>
                      </div>
                      <Text style={{ fontSize: '14px', color: '#64748B', display: 'block' }}>
                        {intl.formatMessage({ id: 'onboarding.survey.finalStep.heroSubtitle' })}
                      </Text>
                    </div>

                    {/* Urgency Banner — only shown once trial status has loaded
                        with real metadata values. */}
                    {trialParams && (
                      <Alert
                        type="warning"
                        showIcon
                        icon={<ClockCircleOutlined style={{ color: '#d97706' }} />}
                        message={
                          <span style={{ fontWeight: 700, color: '#92400e', fontSize: '15px' }}>
                            {intl.formatMessage(
                              { id: 'onboarding.survey.finalStep.urgencyTitle' },
                              { responseCheckDays: trialParams.responseCheckDays },
                            )}
                          </span>
                        }
                        description={
                          <span style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.6 }}>
                            {intl.formatMessage(
                              { id: 'onboarding.survey.finalStep.urgencyDescription' },
                              {
                                requiredResponses: trialParams.requiredResponses,
                                responseCheckDays: trialParams.responseCheckDays,
                                totalDays: trialParams.totalDays,
                              },
                            )}
                          </span>
                        }
                        style={{
                          marginBottom: '20px',
                          background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)',
                          border: '1px solid #fbbf24',
                          borderRadius: '10px',
                        }}
                      />
                    )}

                    {/* Focus Card - The one survey that matters today */}
                    {allCustomersSurvey && (
                      <Card
                        style={{
                          marginBottom: '20px',
                          background: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: 16,
                          boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                        }}
                        styles={{ body: { padding: 0 } }}
                      >
                        <div style={{ padding: '32px' }}>
                          <Tag color="#ef4444" style={{ fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px', marginBottom: '12px', border: 'none' }}>
                            {intl.formatMessage({ id: 'onboarding.survey.finalStep.focusCardEyebrow' })}
                          </Tag>
                          <Title level={4} style={{ margin: '0 0 6px 0', color: '#18216d', fontWeight: 700 }}>
                            {intl.formatMessage({ id: 'onboarding.survey.finalStep.focusCardTitle' })}
                          </Title>
                          <Text style={{ fontSize: '13px', display: 'block', marginBottom: '18px', color: '#0F172A', lineHeight: 1.5 }}>
                            {intl.formatMessage(
                              { id: 'onboarding.survey.finalStep.focusCardDescription' },
                              { b: (chunks: React.ReactNode) => <strong>{chunks}</strong> },
                            )}
                          </Text>

                          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: '12px', padding: '14px 16px', backgroundColor: '#F8FAFC', borderRadius: '8px', marginBottom: '12px', border: '1px solid #E5E7EB' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                              <LinkOutlined style={{ color: '#64748B', fontSize: '16px', flexShrink: 0 }} />
                              <Input
                                value={Utils.getPublicSurveyUrl(allCustomersSurvey.hash)}
                                readOnly
                                style={{
                                  flex: 1,
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  border: 'none',
                                  background: 'transparent',
                                  padding: 0,
                                }}
                              />
                            </div>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                const publicUrl = Utils.getPublicSurveyUrl(allCustomersSurvey.hash);
                                navigator.clipboard.writeText(publicUrl).then(() => {
                                  message.success(intl.formatMessage({ id: 'onboarding.survey.finalStep.linkCopiedSuccess' }));
                                });
                              }}
                            >
                              {intl.formatMessage({ id: 'onboarding.survey.finalStep.copyLinkButton' })}
                            </Button>
                          </div>

                          <Button
                            type="primary"
                            size="large"
                            icon={<MailOutlined />}
                            onClick={() => handleCopyEmailTemplate(allCustomersSurvey.hash)}
                            block
                            style={{
                              background: 'linear-gradient(135deg, #18216d 0%, #312e81 100%)',
                              border: 'none',
                              minHeight: '52px',
                              height: 'auto',
                              fontWeight: 700,
                              fontSize: isMobile ? '14px' : '15px',
                              borderRadius: '10px',
                              boxShadow: '0 4px 12px rgba(24, 33, 109, 0.25)',
                              whiteSpace: 'normal',
                              padding: '12px 16px',
                            }}
                          >
                            {intl.formatMessage({ id: 'onboarding.survey.finalStep.copyTemplateButton' })}
                          </Button>
                        </div>
                      </Card>
                    )}

                    {/* Getting-started guide: roadmap + incentive advice,
                        shared verbatim with the marketer-view empty state. */}
                    <GettingStartedGuide />

                    {/* Pro Tip - kept for merge-tag tracking guidance */}
                    <Alert
                      type="warning"
                      showIcon
                      message={intl.formatMessage({ id: 'onboarding.survey.proTipTitle' })}
                      description={
                        <div style={{ fontSize: '13px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            {intl.formatMessage({ id: 'onboarding.survey.proTipDescription' })} <Text code>{`{customer_identifier}`}</Text> {intl.formatMessage({ id: 'onboarding.survey.proTipWithPlatform' })}
                          </div>
                          <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                            <li><strong>Klaviyo:</strong> {intl.formatMessage({ id: 'onboarding.survey.proTipReplaceWith' })} <Text code>{`{{ email }}`}</Text></li>
                            <li><strong>Mailchimp:</strong> {intl.formatMessage({ id: 'onboarding.survey.proTipReplaceWith' })} <Text code>*|EMAIL|*</Text></li>
                            <li><strong>HubSpot:</strong> {intl.formatMessage({ id: 'onboarding.survey.proTipReplaceWith' })} <Text code>{`{{ contact.email }}`}</Text></li>
                          </ul>
                          <div style={{ marginTop: '8px', color: '#94A3B8' }}>
                            {intl.formatMessage({ id: 'onboarding.survey.proTipTracking' })}
                          </div>
                        </div>
                      }
                      style={{ background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '20px' }}
                    />

                    {/* What's next — collapsed disclosure containing the recent purchasers survey */}
                    {recentPurchasersSurvey && (
                      <Collapse
                        ghost
                        style={{
                          marginBottom: '20px',
                          background: 'rgba(245, 158, 11, 0.04)',
                          border: '1px dashed #fbbf24',
                          borderRadius: '10px',
                        }}
                        items={[{
                          key: 'whats-next',
                          label: (
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>
                              {intl.formatMessage({ id: 'onboarding.survey.finalStep.whatsNextTitle' })}
                            </span>
                          ),
                          children: (
                            <div>
                              <Paragraph style={{ fontSize: '13px', color: '#0F172A', marginBottom: '14px', lineHeight: 1.6 }}>
                                {intl.formatMessage({ id: 'onboarding.survey.finalStep.whatsNextDescription' })}
                              </Paragraph>
                              <Text style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: '8px' }}>
                                {intl.formatMessage({ id: 'onboarding.survey.finalStep.whatsNextSurveyLabel' })}
                              </Text>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <LinkOutlined style={{ color: '#64748B', fontSize: '14px' }} />
                                <Input
                                  value={Utils.getPublicSurveyUrl(recentPurchasersSurvey.hash)}
                                  readOnly
                                  style={{
                                    flex: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                  }}
                                />
                                <Button
                                  size="small"
                                  icon={<CopyOutlined />}
                                  onClick={() => {
                                    const publicUrl = Utils.getPublicSurveyUrl(recentPurchasersSurvey.hash);
                                    navigator.clipboard.writeText(publicUrl).then(() => {
                                      message.success(intl.formatMessage({ id: 'onboarding.survey.finalStep.linkCopiedSuccess' }));
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          ),
                        }]}
                      />
                    )}

                    {/* Precommit checkbox + CTAs — hidden when the step is recalled read-only */}
                    {!embedded && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div style={{
                        padding: '14px 16px',
                        background: '#FEF3C7',
                        border: '1px solid #E5E7EB',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                      }}>
                        <Checkbox
                          checked={firstBatchCommitted}
                          onChange={(e) => setFirstBatchCommitted(e.target.checked)}
                          style={{ marginTop: '2px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#18216d', fontSize: '14px' }}>
                            {intl.formatMessage({ id: 'onboarding.survey.finalStep.precommitLabel' })}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                            {intl.formatMessage({ id: 'onboarding.survey.finalStep.precommitHint' })}
                          </div>
                        </div>
                      </div>

                      <Button
                        type="default"
                        size="large"
                        icon={<EditOutlined />}
                        onClick={handleViewSurveyDetails}
                        style={{
                          height: '48px',
                          fontWeight: '600',
                          fontSize: '16px',
                          border: '2px solid #F59E0B',
                          borderRadius: 10,
                          width: '100%',
                        }}
                      >
                        {intl.formatMessage({ id: 'onboarding.survey.viewAllSurveys' })}
                      </Button>

                      <Button
                        type="primary"
                        size="large"
                        icon={<CheckCircleOutlined />}
                        onClick={handleFinalComplete}
                        style={{
                          background: '#F59E0B',
                          border: 'none',
                          height: '48px',
                          fontWeight: '600',
                          fontSize: '16px',
                          width: '100%',
                          color: 'white',
                          borderRadius: 10,
                        }}
                        className=""
                      >
                        {intl.formatMessage({ id: 'onboarding.survey.completeSetup' })}
                      </Button>
                    </Space>
                    )}
                  </>
                );
              })() : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                  <Spin />
                  <div style={{ marginTop: '16px' }}>{intl.formatMessage({ id: 'onboarding.survey.creatingSurveys' })}</div>
                </div>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
  ];

  return (
    <div className="revolutionary-dashboard gpu-accelerated" style={{ padding: isMobile ? '12px' : '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {!embedded && (<>
      {/* Hero styled after the marketing site's dark "trial" banner card
          (bg-slate-900, 2px amber border, amber glow) so the site -> wizard
          journey feels continuous. Elevated one shade (#1E293B) above the
          #0F172A page so it reads as a distinct card. */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px',
        padding: '36px 24px',
        background: '#1E293B',
        border: '2px solid #F59E0B',
        boxShadow: '0 16px 44px rgba(245,158,11,.28)',
        borderRadius: 16,
        position: 'relative',
        overflow: 'hidden'
      }}
      className=""
      >
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
            <FireOutlined style={{ fontSize: '32px', color: '#F59E0B' }} />
            <Title level={2} style={{
              color: '#FFFFFF',
              margin: 0,
              fontSize: '30px',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
              {intl.formatMessage({ id: 'onboarding.hero.titleLead' })}{' '}
              <span style={{ color: '#F59E0B' }}>
                {intl.formatMessage({ id: 'onboarding.hero.titleAccent' })}
              </span>
            </Title>
          </div>

          <Paragraph style={{
            fontSize: '16px',
            color: '#CBD5E1',
            maxWidth: '700px',
            margin: '0 auto 16px',
            fontWeight: '500'
          }}>
            {intl.formatMessage({ id: 'onboarding.hero.subtitle' })}
          </Paragraph>

          {/* Unified amber "chips" (was green/amber/orange) — one cohesive row,
              styled to read on the dark hero like the site's amber eyebrow. */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {[
              'onboarding.hero.badgeSimplified',
              'onboarding.hero.badgeDefaults',
              'onboarding.hero.badgeControl',
            ].map((id) => (
              <span
                key={id}
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FBBF24',
                  background: 'rgba(245,158,11,0.12)',
                  border: '1px solid rgba(245,158,11,0.35)',
                  borderRadius: 999,
                  padding: '3px 12px',
                }}
              >
                {intl.formatMessage({ id })}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Steps with Enhanced Styling */}
      <Card
        className=""
        style={{
          marginBottom: '32px',
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
      >
        <Steps 
          current={currentStep} 
          size="default"
          style={{ padding: '16px 0' }}
          onChange={(step) => {
             if (step < currentStep) {
                 setCurrentStep(step);
             } else if (step === 1 && brandNameConfirmed) {
                 setCurrentStep(step);
             } else if (step === 2 && surveyCreated) {
                 setCurrentStep(step);
             }
          }}
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={
                <span style={{ 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: step.completed ? '#52c41a' : index === currentStep ? '#F59E0B' : '#94A3B8'
                }}>
                  {step.title}
                </span>
              }
              icon={
                step.completed ? (
                  <div style={{
                    background: '#F59E0B',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircleOutlined style={{ color: 'white', fontSize: '18px' }} />
                  </div>
                ) : (
                  <div style={{
                    background: index === currentStep
                      ? '#F59E0B'
                      : '#F1F5F9',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(step.icon, { 
                      style: { 
                        ...step.icon.props.style,
                        color: index === currentStep ? 'white' : '#94A3B8' 
                      }
                    })}
                  </div>
                )
              }
              status={step.completed ? 'finish' : index === currentStep ? 'process' : 'wait'}
            />
          ))}
        </Steps>
      </Card>
      </>)}

      {/* Step Content */}
      <div key={currentStep} style={{ minHeight: '400px' }}>
        {steps[currentStep]?.content}
      </div>

      <style>{`
        .revolutionary-step-card {
          border: none !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
        }
        .revolutionary-step-card:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
        }
        .revolutionary-step-card .ant-card-body {
          padding: 0 !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
        .ant-steps-item-wait .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};